import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import Link from '@wix/thunderbolt-elements/components/Link';
import { TestIds } from '../constants';
import type {
  TextParticlesImperativeActions,
  TextParticlesProps,
} from '../TextParticles.types';
import semanticClassNames from '../TextParticles.semanticClassNames';
import { useComputedStyle } from './hooks/useComputedStyle';
import { useImageLoader } from './hooks/useImageLoader';
import { useParticleRenderer } from './hooks/useParticleRenderer';
import { useWebWorker } from './hooks/useWebWorker';
import styles from './TextParticles.scss';
import { AnimationType, type AnimationState } from './TextParticles.types';
import { getCanvasDimensions } from './utils/canvas-utils';
import { getMainThreadMessages } from './worker/messages';
import {
  getEffectConfiguration,
  hasAnimationType,
  isPerlinEffect,
} from './selectors';

const TextParticles: React.ForwardRefRenderFunction<
  TextParticlesImperativeActions,
  TextParticlesProps
> = (props, ref) => {
  const {
    id,
    text,
    componentViewMode,
    isPresetPreview,
    reducedMotion: reducedMotionProp = false,
    className,
    customClassNames = [],
    tagName,
    textDirection,
    link,
    isDecorative,
    onPlay,
    onStop,
    onMouseEnter,
    onMouseLeave,
    onPause,
  } = props;
  const SelectableTextElement = (tagName || 'p') as React.ElementType;
  const isEditor = componentViewMode === 'editor';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLHeadingElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const computedStyle = useComputedStyle(id, rootRef, isEditor);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [showSelectableText, setShowSelectableText] = useState(true);

  const applyReducedMotion = (reducedMotion && !isEditor) || isPresetPreview;
  const worker = useWebWorker();
  const isAnimationReady = worker && computedStyle;

  useEffect(() => {
    setReducedMotion(reducedMotionProp);
  }, [reducedMotionProp]);

  const effectConfiguration = useMemo(
    () => getEffectConfiguration(computedStyle, text, isEditor),
    [computedStyle, text, isEditor],
  );

  const animationType = hasAnimationType(effectConfiguration)
    ? effectConfiguration.animationType
    : null;

  useEffect(() => {
    if (animationType === AnimationType.OUT) {
      setShowSelectableText(true);
    }
  }, [animationType]);

  const updateAnimationState = useCallback(
    (
      canvas: React.MutableRefObject<HTMLCanvasElement | null>,
      newAnimationState: AnimationState,
    ) => {
      if (canvas.current) {
        canvas.current.setAttribute(
          TestIds.viewer.animationState,
          newAnimationState,
        );
      }

      if (!rootRef.current) {
        return;
      }

      const hasDoneClass = rootRef.current.classList.contains(
        formatClassNames(semanticClassNames.rootDone),
      );

      if (
        hasDoneClass &&
        newAnimationState !== 'finished' &&
        newAnimationState !== 'paused'
      ) {
        rootRef.current.classList.remove(
          formatClassNames(semanticClassNames.rootDone),
        );
      }

      if (!hasDoneClass && newAnimationState === 'finished') {
        rootRef.current.classList.add(
          formatClassNames(semanticClassNames.rootDone),
        );
      }
    },
    [],
  );

  const canvasDimensions = useMemo(
    () => getCanvasDimensions(computedStyle?.boundingBox),
    [computedStyle?.boundingBox],
  );
  const image = useImageLoader({
    canvasDimensions,
    computedStyle,
    text,
    textDirection,
  });
  const messages = useMemo(() => getMainThreadMessages(id), [id]);

  const onAnimationEnd = useCallback(() => {
    if (isEditor) {
      updateAnimationState(canvasRef, 'preview');
      worker?.postMessage(messages.getSetPreviewMessage());
    } else {
      updateAnimationState(canvasRef, 'finished');
    }

    if (animationType) {
      setShowSelectableText(animationType === AnimationType.IN);
    }
  }, [
    worker,
    messages,
    isEditor,
    updateAnimationState,
    animationType,
    setShowSelectableText,
  ]);

  const onAnimationStopped = useCallback(() => {
    if (isEditor) {
      updateAnimationState(canvasRef, 'preview');
      worker?.postMessage(messages.getSetPreviewMessage());
    } else {
      updateAnimationState(canvasRef, 'stopped');
    }

    if (animationType) {
      setShowSelectableText(animationType === AnimationType.OUT);
    }
  }, [isEditor, animationType, updateAnimationState, worker, messages]);

  const onInitialized = useCallback(() => {
    if (isEditor) {
      updateAnimationState(canvasRef, 'preview');
      worker?.postMessage(messages.getSetPreviewMessage());
    }
  }, [isEditor, updateAnimationState, worker, messages]);

  const { play, stop, pause } = useParticleRenderer({
    effectConfiguration,
    image,
    canvasRef,
    onAnimationEnd,
    componentId: id,
    worker,
    messages,
    onInitialized,
    onAnimationStopped,
  });

  const handlePlay = useCallback(async () => {
    updateAnimationState(canvasRef, 'playing');
    onPlay?.({ type: 'onPlay', compId: id });

    if (animationType) {
      setShowSelectableText(animationType === AnimationType.OUT);
    }

    await play();
  }, [play, updateAnimationState, onPlay, id, animationType]);

  const handlePause = useCallback(async () => {
    onPause?.({ type: 'onPause', compId: id });
    await pause();
    updateAnimationState(canvasRef, 'paused');
  }, [pause, updateAnimationState, onPause, id]);

  const handleStop = useCallback(async () => {
    onStop?.({ type: 'onStop', compId: id });
    await stop();
    updateAnimationState(canvasRef, 'stopped');

    if (animationType) {
      setShowSelectableText(animationType === AnimationType.OUT);
    }

    onAnimationStopped();
  }, [
    onStop,
    id,
    stop,
    updateAnimationState,
    animationType,
    onAnimationStopped,
  ]);

  const handleTextClick = useCallback(async () => {
    if (
      !effectConfiguration ||
      !isPerlinEffect(effectConfiguration) ||
      !isAnimationReady ||
      applyReducedMotion
    ) {
      return;
    }

    const currentState = canvasRef.current?.getAttribute(
      TestIds.viewer.animationState,
    );
    const isPaused = currentState !== 'playing';

    if (isPaused) {
      await handlePlay();
    } else {
      await handlePause();
    }
  }, [
    effectConfiguration,
    isAnimationReady,
    applyReducedMotion,
    handlePlay,
    handlePause,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      play: handlePlay,
      stop: handleStop,
      pause: handlePause,
    }),
    [handlePlay, handleStop, handlePause],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const textElement = textRef.current;

    if (isEditor || !isAnimationReady || !canvas || !textElement) {
      return;
    }

    const observer = new IntersectionObserver(
      async entries => {
        if (canvas.getAttribute(TestIds.viewer.animationState) === 'finished') {
          return;
        }

        for (const entry of entries) {
          if (entry.isIntersecting) {
            await handlePlay();
          } else {
            await handleStop();
          }
        }
      },
      { root: null, threshold: 0.5 },
    );

    observer.observe(textElement);

    return () => {
      observer.unobserve(textElement);
    };
  }, [isEditor, handlePlay, handleStop, isAnimationReady]);

  useEffect(
    () => () => {
      worker?.postMessage(messages.getTerminateInstanceMessage());
    },
    [id, worker, messages],
  );

  return (
    <div
      {...(isDecorative && { 'aria-hidden': true })}
      id={id}
      ref={rootRef}
      className={classNames(
        styles.root,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      dir={textDirection}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {applyReducedMotion ? null : (
        <canvas
          aria-hidden={true}
          ref={canvasRef}
          className={styles.canvas}
          data-testid={TestIds.viewer.canvas}
          style={
            canvasDimensions
              ? {
                  width: canvasDimensions.displayWidth,
                  height: canvasDimensions.displayHeight,
                }
              : undefined
          }
        />
      )}
      {link ? (
        <Link
          {...link}
          className={styles.link}
          dataTestId={TestIds.viewer.link}
        >
          <SelectableTextElement
            ref={textRef}
            data-testid={TestIds.viewer.text}
            onClick={
              !isEditor && !isPresetPreview ? handleTextClick : undefined
            }
            className={classNames(styles.selectableText, {
              [styles.reducedMotion]: applyReducedMotion,
              [styles.hidden]: !showSelectableText,
            })}
          >
            {text}
          </SelectableTextElement>
        </Link>
      ) : (
        <SelectableTextElement
          ref={textRef}
          data-testid={TestIds.viewer.text}
          onClick={!isEditor && !isPresetPreview ? handleTextClick : undefined}
          className={classNames(styles.selectableText, {
            [styles.reducedMotion]: applyReducedMotion,
            [styles.hidden]: !showSelectableText,
          })}
        >
          {text}
        </SelectableTextElement>
      )}
    </div>
  );
};

export default React.forwardRef(TextParticles);
